import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import { ContextMenuView } from './ContextMenuView';
import type { ContextMenuApi, ContextMenuEntry } from './types';

const Ctx = createContext<ContextMenuApi | null>(null);

interface OpenState {
  x: number;
  y: number;
  items: ContextMenuEntry[];
}

export interface ContextMenuProviderProps {
  children: ReactNode;
}

/**
 * Mount once near the app root. Provides {@link useContextMenu} and renders the
 * active menu in a portal. Also installs a capture-phase `contextmenu` listener
 * on `document` that ALWAYS suppresses the native browser menu — regions that
 * want a menu call `openContextMenu` from their own `onContextMenu`; everywhere
 * else simply shows nothing (native already suppressed).
 */
export function ContextMenuProvider({ children }: ContextMenuProviderProps): ReactElement {
  const [open, setOpen] = useState<OpenState | null>(null);

  const openContextMenu = useCallback((x: number, y: number, items: ContextMenuEntry[]): void => {
    // Opening with no actionable entries is a no-op (still suppresses native).
    if (items.length === 0) {
      setOpen(null);
      return;
    }
    setOpen({ x, y, items });
  }, []);

  const closeContextMenu = useCallback((): void => setOpen(null), []);

  // Global native-menu suppression (capture phase, document root). preventDefault
  // is unconditional; consumers opt INTO a custom menu via their own handler.
  useEffect(() => {
    const suppress = (e: MouseEvent): void => {
      e.preventDefault();
    };
    document.addEventListener('contextmenu', suppress, true);
    return () => document.removeEventListener('contextmenu', suppress, true);
  }, []);

  const api = useMemo<ContextMenuApi>(
    () => ({ openContextMenu, closeContextMenu }),
    [openContextMenu, closeContextMenu],
  );

  return (
    <Ctx.Provider value={api}>
      {children}
      {open && (
        <ContextMenuView x={open.x} y={open.y} items={open.items} onClose={closeContextMenu} />
      )}
    </Ctx.Provider>
  );
}

/**
 * Access the context-menu API. Returns a no-op implementation when used outside
 * a provider (e.g. an isolated component test) so callers never need to guard.
 */
export function useContextMenu(): ContextMenuApi {
  const api = useContext(Ctx);
  return api ?? NOOP_API;
}

const NOOP_API: ContextMenuApi = {
  openContextMenu: () => {},
  closeContextMenu: () => {},
};
